import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useAnimation, Variants } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styled, { keyframes, css } from 'styled-components';
import { useLanguage } from '../components/Navbar';
import SEO, { getHomeMetadata } from '../components/SEO';

// Import images
import heroBackground from '../assets/images/hero-bg.webp';
import profileImage from '../assets/images/BARKIN SAYIN.webp';
import visionImage from '../assets/images/vision.webp';
import tradeOnTvImg from '../assets/images/projects/trade-on-tv.webp';
import tradeOnTVImage from '../assets/images/tradeontv.webp';
import placeholderCompanyImg from '../assets/images/netflipp.webp';
import appleLogo from '../assets/images/apple_731985.webp';
import partner1Logo from '../assets/images/partner1-logo.webp';
import partner2Logo from '../assets/images/partner2-logo.webp';
import partner4Logo from '../assets/images/partner4-logo.webp';
import partner5Logo from '../assets/images/partner5-logo.webp';
import partner6Logo from '../assets/images/partner6-logo.webp';
import partner7Logo from '../assets/images/partner7-logo.webp';
import totBlackLogo from '../assets/images/tot-black.webp';
// Video path

// Animations
const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-15px); }
  100% { transform: translateY(0px); }
`;

const shineAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const breatheAnimation = keyframes`
  0% { opacity: 0.7; }
  50% { opacity: 1; }
  100% { opacity: 0.7; }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Additional animations
const shine = keyframes`
  0% { background-position: -100% 0; }
  100% { background-position: 200% 0; }
`;

const wave = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-15px); }
  100% { transform: translateY(0); }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

// Rename the keyframes version of fadeIn
const fadeInKeyframes = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Add new animations
const fadeOutHero = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

// Styled components
const PageWrapper = styled.div`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  color: #333;
  background-color: #fff;
`;

const HomeContainer = styled.div`
  width: 100%;
  overflow-x: hidden;
  background-color: #ffffff;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const HeroSection = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5%;
  overflow: hidden;
  background: #000000;
  background-size: cover;
  background-position: center;
  
  @media (max-width: 768px) {
    min-height: 40vh;
    height: auto;
    padding-top: 60px;
    padding-bottom: 0;
    max-height: none;
  }
`;

const HeroImageContainer = styled.div<{ $isHiding: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
  opacity: ${props => props.$isHiding ? '0.2' : '1'};
  transition: opacity 0.5s ease;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 25% 50%, rgba(30, 144, 255, 0.15), transparent 55%),
      radial-gradient(circle at 75% 50%, rgba(255, 140, 0, 0.15), transparent 55%);
    z-index: 0;
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 40px;
    background: linear-gradient(to top, 
      rgba(0,0,0,0.8) 0%, 
      rgba(0,0,0,0.4) 60%, 
      rgba(0,0,0,0) 100%);
    z-index: 1;
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    align-items: flex-end;
    justify-content: center;
    height: 100%;
    
    &::after {
      height: 40px;
      background: linear-gradient(to top, 
        rgba(0,0,0,0.7) 0%, 
        rgba(0,0,0,0.3) 60%, 
        rgba(0,0,0,0) 100%);
    }
  }
`;

const HeroPngImage = styled.img`
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
  opacity: 0.8;
  position: relative;
  z-index: 0;
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    height: auto;
    object-fit: contain;
    object-position: bottom;
    margin-bottom: 0;
    transform: none;
  }
`;

const HeroVideoContainer = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 20;
  background-color: transparent;
  opacity: ${props => props.$isVisible ? '1' : '0'};
  visibility: ${props => props.$isVisible ? 'visible' : 'hidden'};
  transition: opacity 0.5s ease, visibility 0.5s ease;
  cursor: pointer; /* Tıklanabilir olduğunu göstermek için imleç */
`;

const VideoElement = styled.video`
  width: 80%;
  height: auto;
  max-height: 80vh;
  object-fit: contain;
  background-color: #000;
  outline: none !important;
  pointer-events: none; /* Videoya tıklamayı tamamen engeller */
  user-select: none; /* Metni seçmeyi engeller */
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  
  &::-webkit-media-controls-panel,
  &::-webkit-media-controls-play-button,
  &::-webkit-media-controls-start-playback-button,
  &::-webkit-media-controls-volume-slider,
  &::-webkit-media-controls-cast-button,
  &::-webkit-media-controls-fullscreen-button,
  &::-webkit-media-controls-timeline {
    display: none !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }
  
  @media (max-width: 768px) {
    width: 90%;
    object-fit: contain;
  }
`;

const VideoPopup = styled.div`
  position: absolute;
  left: 30px;
  bottom: 40px;
  width: 280px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  z-index: 10;
  background-color: rgba(0, 0, 0, 0.8);
  padding: 15px;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    left: 10px;
    bottom: 10px;
    width: 120px;
    padding: 6px;
    border-radius: 8px;
  }
`;

const VideoPopupTitle = styled.h3`
  color: white;
  font-size: 16px;
  margin: 0 0 15px 0;
  text-align: center;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 12px;
    margin: 0 0 6px 0;
    font-weight: 500;
  }
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const WatchButton = styled.button`
  width: 100%;
  background-color: rgba(255, 255, 255, 0.9);
  color: black;
  border: none;
  padding: 10px 16px;
  border-radius: 4px;
  
  @media (max-width: 768px) {
    padding: 4px 6px;
    border-radius: 3px;
    font-size: 10px;
  }
  
  svg {
    margin-right: 8px;
    
    @media (max-width: 768px) {
      width: 10px;
      height: 10px;
      margin-right: 4px;
    }
  }
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 15px;
  align-self: flex-start;
  text-transform: uppercase;
  letter-spacing: 0.01em;
  
  &:hover {
    background-color: white;
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
  
  svg {
    margin-right: 8px;
  }
`;

const VideoControls = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 15px;
  z-index: 30;
  background-color: rgba(0, 0, 0, 0.6);
  padding: 8px 16px;
  border-radius: 8px;
`;

const VideoCloseBtn = styled.button`
  background: none;
  color: white;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.3s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const VolumeControl = styled.div`
  display: flex;
  align-items: center;
  height: 36px;
  
  input {
    width: 80px;
    margin-left: 8px;
    cursor: pointer;
    accent-color: white;
  }
  
  svg path {
    stroke: white;
  }
`;

// Video kalite seçici styled component
const QualitySelector = styled.select`
  background: transparent;
  color: white;
  border: none;
  outline: none;
  cursor: pointer;
  option {
    background: white;
    color: black;
  }
`;

// Bu eski buton artık kullanılmıyor

const FloatingElement = styled(motion.div)`
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(45deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05));
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  z-index: 2;
  
  &.small {
    width: 50px;
    height: 50px;
    animation: ${wave} 6s ease-in-out infinite;
  }
  
  &.medium {
    width: 100px;
    height: 100px;
    animation: ${wave} 8s ease-in-out infinite;
  }
  
  &.large {
    width: 150px;
    height: 150px;
    animation: ${wave} 10s ease-in-out infinite;
  }
  
  &.top-left {
    top: 15%;
    left: 10%;
  }
  
  &.top-right {
    top: 25%;
    right: 15%;
  }
  
  &.bottom-left {
    bottom: 20%;
    left: 15%;
  }
  
  &.bottom-right {
    bottom: 15%;
    right: 10%;
  }
`;

const GlowingLine = styled(motion.div)`
  position: absolute;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), rgba(255,255,255,0.6), rgba(255,255,255,0.2), transparent);
  background-size: 200% 100%;
  animation: ${shine} 4s linear infinite;
  z-index: 2;
  
  &.top {
    top: 20%;
    left: 0;
    right: 0;
    width: 100%;
  }
  
  &.bottom {
    bottom: 25%;
    left: 0;
    right: 0;
    width: 100%;
  }
`;

const HeroContent = styled(motion.div)`
  display: block;
  text-align: center;
  max-width: 900px;
  width: 100%;
  z-index: 30; /* Ensure content stays above video */
  color: #ffffff;
  padding: 0;
  position: absolute;
  left: 0;
  right: 0;
  top: 15%;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    top: 25%;
    width: 90%;
    background: transparent;
    padding: 12px 15px;
    border-radius: 8px;
    backdrop-filter: none;
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(1.2rem, 1.8vw, 1.6rem);
  font-weight: 800;
  margin-bottom: 0;
  line-height: 1.1;
  color: rgba(255, 255, 255, 1); /* Full opacity for the text */
  letter-spacing: 0.03em;
  text-transform: uppercase;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
  width: 100%;
  max-width: 100%;
  text-align: center;
  overflow: hidden;
  z-index: 30; /* Ensure text stays above video */
  
  .text-reveal {
    display: inline-block;
  }
  
  .highlight {
    color: rgba(255, 255, 255, 1);
    font-weight: 900;
  }
  
  @media (max-width: 768px) {
    font-size: clamp(0.9rem, 1.3vw, 1.1rem);
    letter-spacing: 0.02em;
    line-height: 1;
  }
`;

const QuoteMarks = styled.span`
  display: block;
  font-family: Georgia, serif;
  font-size: 4rem;
  line-height: 0;
  color: rgba(255, 255, 255, 0.2);
  position: absolute;
  
  &.left {
    top: -1.5rem;
    left: -1.5rem;
  }
  
  &.right {
    bottom: -1rem;
    right: -1.5rem;
    transform: rotate(180deg);
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(1.1rem, 1.6vw, 1.4rem);
  line-height: 1.8;
  margin-bottom: 3.5rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.85);
  
  @media (max-width: 768px) {
    font-size: clamp(1rem, 1.5vw, 1.2rem);
  }
`;

const ButtonContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-top: 2.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
`;

const PrimaryButton = styled(motion.button)`
  padding: 1.1rem 2.8rem;
  background: white;
  color: black;
  font-weight: 600;
  font-size: 0.95rem;
  border: none;
  border-radius: 0;
  cursor: pointer;
  transition: background-color 0.2s ease;
  letter-spacing: 0.1em;
  
  &:hover {
    background: rgba(255, 255, 255, 0.8);
  }
  
  a {
    color: black;
    text-decoration: none;
  }
`;

const SecondaryButton = styled(motion.button)`
  padding: 1.1rem 2.8rem;
  background: transparent;
  color: white;
  font-weight: 600;
  font-size: 0.95rem;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 0;
  cursor: pointer;
  transition: background-color 0.2s ease;
  letter-spacing: 0.1em;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  a {
    color: white;
    text-decoration: none;
  }
`;

const Decorator = styled(motion.div)`
  position: absolute;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  z-index: 1;
  
  &.top-right {
    top: -100px;
    right: -100px;
    width: 400px;
    height: 400px;
  }
  
  &.bottom-left {
    bottom: -150px;
    left: -150px;
    width: 500px;
    height: 500px;
    border-width: 2px;
  }
  
  &.middle-center {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 800px;
    height: 800px;
    border-width: 1px;
    opacity: 0.08;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.8rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  font-size: 1.2rem;
  line-height: 1.6;
  margin-bottom: 3rem;
  text-align: center;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  color: #555;
`;

const PhilosophySection = styled.section`
  padding: 100px 0 120px;
  background: linear-gradient(to bottom, #f5f5f5, #ffffff);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 90% 10%, rgba(150, 150, 150, 0.05), rgba(0,0,0,0) 50%),
      linear-gradient(to bottom right, rgba(150, 150, 150, 0.03), rgba(0,0,0,0) 70%);
    pointer-events: none;
    z-index: 0;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100px;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(13, 13, 13, 0.05));
    z-index: 1;
  }
`;

// Pattern arka plan için ek bir katman oluşturalım
const PatternBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.05' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.3;
  pointer-events: none;
  z-index: 0;
`;

const PhilosophyContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  margin-top: 0;
  padding: 0 30px;
  display: flex;
  flex-direction: row;
  gap: 4rem;
  align-items: flex-start;
  position: relative;
  z-index: 10;
  
  @media (max-width: 992px) {
    flex-direction: column;
    text-align: center;
    gap: 3rem;
    margin-top: 0;
    align-items: center;
  }
`;

const PhilosophyContent = styled.div`
  max-width: 600px;
  position: relative;
  padding-top: 55px;
  
  &::before {
    content: '"';
    position: absolute;
    top: -20px;
    left: -40px;
    font-size: 150px;
    font-family: Georgia, serif;
    line-height: 1;
    color: rgba(200, 200, 200, 0.15);
    z-index: -1;
  }
  
  @media (max-width: 992px) {
    max-width: 100%;
    padding-top: 0;
    
    &::before {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

const PhilosophyImageWrapper = styled(motion.div)`
  position: relative;
  width: 100%;
  max-width: 500px;
  height: 550px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
  margin-top: 7px;
  
  &:hover {
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
  }
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, 
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0) 50%,
      rgba(0, 0, 0, 0.2) 100%
    );
    z-index: 1;
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border: 2px solid rgba(200, 200, 200, 0.2);
    border-radius: 12px;
    z-index: 2;
    opacity: 0.7;
  }
  
  @media (max-width: 992px) {
    height: 450px;
    max-width: 600px;
    margin: 0 auto;
    margin-top: 0;
  }
  
  @media (max-width: 768px) {
    height: 400px;
    margin-top: 0;
  }
  
  @media (max-width: 480px) {
    height: 350px;
    margin-top: 0;
  }
`;

const PhilosophyImage = styled(motion.div)`
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center top;
`;

const PhilosophyQuote = styled.blockquote`
  font-size: clamp(1.4rem, 3vw, 1.8rem);
  font-weight: 300;
  font-style: italic;
  line-height: 1.5;
  color: #333;
  margin: 40px 0;
  padding: 0 0 0 25px;
  border-left: 1px solid rgba(150, 150, 150, 0.5);
  
  @media (max-width: 768px) {
    font-size: clamp(1.2rem, 2.5vw, 1.6rem);
    margin: 30px 0;
    padding-left: 20px;
  }
`;

const SectionHeading = styled(motion.h2)<{ $themeMode?: 'light' | 'dark' }>`
  font-size: clamp(2.2rem, 4vw, 2.8rem);
  font-weight: 300;
  margin-bottom: 2.5rem;
  color: ${props => props.$themeMode === 'dark' ? '#ffffff' : '#222222'};
  line-height: 1.2;
  position: relative;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  text-align: left;
  
  &::after {
    content: '';
    display: block;
    width: 60px;
    height: 1px;
    background: ${props => props.$themeMode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(150, 150, 150, 0.5)'};
    margin-top: 15px;
  }
  
  @media (max-width: 992px) {
    &::after {
{{ ... }}
      margin-left: auto;
      margin-right: auto;
    }
  }
  
  @media (max-width: 768px) {
    font-size: clamp(1.8rem, 3vw, 2.3rem);
    margin-bottom: 2rem;
  }
`;

const PhilosophyText = styled(motion.p)`
  font-size: 1.15rem;
  line-height: 1.8;
  color: #444444;
  margin-bottom: 25px;
  
  @media (max-width: 768px) {
    font-size: 1.05rem;
  }
`;

const PhilosophyHighlight = styled.span`
  color: #666666;
  font-weight: 400;
`;

// Vision Section Styling - Koyu Kalacak
const VisionSection = styled.section`
  padding: 120px 0;
  color: white;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #000000 0%, #0d0d0d 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100px;
    background: linear-gradient(to bottom, rgba(245, 245, 245, 0), rgba(0, 0, 0, 0));
    z-index: 1;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 70% 70%, rgba(150, 150, 150, 0.08), transparent 60%),
      radial-gradient(circle at 30% 30%, rgba(150, 150, 150, 0.05), transparent 60%);
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    padding: 80px 20px;
  }
`;

const VisionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 30px;
  position: relative;
  z-index: 1;
`;

const VisionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 1px;
    background: rgba(255, 255, 255, 0.5);
  }
  
  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const VisionTitle = styled(motion.h2)`
  font-size: clamp(2.5rem, 4vw, 3.2rem);
  font-weight: 300;
  margin-bottom: 25px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #ffffff;
  
  @media (max-width: 768px) {
    font-size: clamp(2rem, 3.5vw, 2.5rem);
  }
`;

const VisionSubtitle = styled(motion.p)`
  font-size: clamp(1.1rem, 1.6vw, 1.2rem);
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.7);
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    font-size: 1.05rem;
  }
`;

const VisionContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3.5rem;
  margin-top: 3rem;
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: 4rem;
  }
`;

const VisionImageWrapper = styled(motion.div)`
  position: relative;
  width: 100%;
  max-width: 500px;
  height: 350px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
  margin-top: 7px;
  
  &:hover {
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
  }
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, 
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0) 50%,
      rgba(0, 0, 0, 0.2) 100%
    );
    z-index: 1;
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    z-index: 2;
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    height: 300px;
    margin-bottom: 20px;
    margin-top: 0;
  }
`;

const VisionImage = styled(motion.div)`
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  
  &:hover {
    /* Transform kaldırıldı - performans için */
  }
`;

const VisionTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  padding-top: 0;
`;

const VisionText = styled(motion.p)`
  font-size: 1.15rem;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 0;
  
  @media (max-width: 768px) {
    font-size: 1.05rem;
  }
`;

const VisionQuote = styled(motion.blockquote)`
  font-size: 1.5rem;
  font-weight: 300;
  font-style: italic;
  line-height: 1.5;
  padding: 25px 0;
  margin: 20px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    padding: 20px 0;
  }
`;

const VisionHighlight = styled.span`
  color: rgba(255, 255, 255, 0.9);
  font-weight: 400;
`;

const VisionCTA = styled(motion.div)`
  margin-top: 40px;
  display: flex;
  justify-content: flex-start;
  
  @media (max-width: 992px) {
    justify-content: center;
  }
`;

// Projects/Brands Section - Açık Tema
const ProjectsSection = styled.section`
  padding: 80px 0;
  background: linear-gradient(to bottom, #f8f8f8, #ffffff);
  position: relative;
  overflow: hidden;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100px;
    background: linear-gradient(to top, rgba(248, 248, 248, 0), rgba(13, 13, 13, 0.05));
    z-index: 1;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23cccccc' fill-opacity='0.05' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.5;
    pointer-events: none;
  }
`;

// Animasyonlu geçiş için dalgalı şekilli divider
const WaveDivider = styled.div`
  position: absolute;
  top: -70px;
  left: 0;
  width: 100%;
  height: 70px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 120' preserveAspectRatio='none'%3E%3Cpath d='M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z' fill='%23ffffff' opacity='.25'%3E%3C/path%3E%3Cpath d='M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z' fill='%23ffffff' opacity='.5'%3E%3C/path%3E%3Cpath d='M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z' fill='%23ffffff'%3E%3C/path%3E%3C/svg%3E");
  background-size: cover;
  z-index: 2;
`;

const BrandsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 50px 30px;
  background: linear-gradient(to right, rgba(255, 255, 255, 0.9), rgba(250, 250, 250, 0.95), rgba(255, 255, 255, 0.9));
  border: 1px solid rgba(200, 200, 200, 0.12);
  border-radius: 16px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.06), 0 5px 15px rgba(0, 0, 0, 0.03);
  position: relative;
  z-index: 10;
  transition: all 0.3s ease;
  overflow: hidden;
`;

const BrandsTitle = styled.h2`
  font-size: 1.5rem;
  color: #333;
  margin-bottom: 60px;
  text-align: center;
  font-weight: 400;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 1px;
    background: rgba(150, 150, 150, 0.5);
  }
`;

const BrandsSlider = styled(motion.div)`
  overflow: hidden;
  position: relative;
  width: 100%;
  padding: 20px 0;
  
  &::before, &::after {
    content: "";
    position: absolute;
    top: 0;
    width: 100px;
    height: 100%;
    z-index: 2;
    pointer-events: none;
  }
  
  &::before {
    left: 0;
    background: linear-gradient(to right, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0));
  }
  
  &::after {
    right: 0;
    background: linear-gradient(to left, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0));
  }
`;

const BrandsTrack = styled(motion.div)`
  display: flex;
  gap: 50px;
  align-items: center;
  width: fit-content;
`;

const BrandLogoWrapper = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(200, 200, 200, 0.1);
  border-radius: 14px;
  padding: 30px;
  width: 180px;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.03);
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-3px);
    border-color: rgba(150, 150, 150, 0.25);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
  }
  
  @media (max-width: 768px) {
    width: 150px;
    height: 150px;
    padding: 20px;
  }
`;

const BrandLogo = styled(motion.img)`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: all 0.2s ease;
  filter: none;
  opacity: 0.9;
  
  ${BrandLogoWrapper}:hover & {
    opacity: 1;
  }
`;

// Animation variants
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3,
    }
  }
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const fadeInLeft = {
  hidden: { x: -50, opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: "easeOut"
    }
  }
};

const fadeInRight = {
  hidden: { x: 50, opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: "easeOut"
    }
  }
};

const floatVariant = {
  initial: { y: 0 },
  animate: { 
    y: [-8, 8, -8], 
    transition: { 
      duration: 6, 
      repeat: Infinity, 
      ease: "easeInOut" 
    }
  }
};

const shineVariant = {
  initial: { backgroundPosition: '200% 0' },
  animate: {
    backgroundPosition: '-200% 0',
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "linear",
    }
  }
};

const breatheVariant = {
  initial: { scale: 1 },
  animate: {
    scale: [1, 1.02, 1],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }
};

const scrollVariant = {
  animate: { 
    x: [0, -1200], 
    transition: { 
      x: {
        repeat: Infinity,
        repeatType: "loop",
        duration: 30,
        ease: "linear"
      }
    }
  }
};

// Hook for scroll animations
const useScrollAnimation = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  return { ref, controls };
};

// Type definitions for languages and translations
type Language = 'en' | 'tr' | 'fr' | 'es' | 'ar';
type TranslationKeys = keyof typeof homeTranslations.en;

// Define translations for the Home page
const homeTranslations = {
  en: {
    heroTitle: "I AM NOT HERE TO PLEASE - I'M HERE TO INSPIRE AND EMPOWER",
    heroSubtitle: "Creating bold solutions that challenge the status quo and build a better future. Through innovation and purpose-driven leadership, turning visionary concepts into transformative realities.",
    readMore: "Read More",
    viewAllProjects: "View All Projects",
    philosophyTitle: "Defining Success on My Own Terms",
    aboutBarkin: "About Barkin Sayin",
    philosophyText1: "Success is not a concept measured solely by numbers or short-term victories; it is a continuous journey sustained by courage, determination, and an unwavering desire for growth. Barkin Sayin believes true success is measured not by perfection, but by authenticity, vision, and the impact left behind.",
    philosophyText2: "As an entrepreneur, Barkin Sayin has always operated with the principle of creating lasting value. For him, innovation is about challenging the status quo, going beyond traditional boundaries, and boldly charting your own path. This journey requires taking risks, learning from every step, and growing with every experience.",
    philosophyText3: "In a rapidly changing world, success is not about making noise or following trends, but creating an impact that resonates over time. Barkin Sayin focuses on building ideas and ventures that defy time by remaining true to core values:",
    quoteText: "Challenge the status quo – true innovation begins where comfort ends.",
    visionTitle: "My Way To The Vision",
    visionSubtitle: "Discover how I approach challenges and create solutions that bring meaningful change through innovative thinking and purposeful leadership.",
    visionText: "With a dedicated focus on entrepreneurial excellence and comprehensive expertise in business management, I've successfully guided numerous projects from concept to implementation. My commitment is to transform innovative ideas into practical solutions. Driven by a vision of creating sustainable value and shaping the future, I maintain a clear perspective on overarching goals and long-term success.",
    visionText2: "I'm committed to sustainable growth and positive impact. My purpose is to leave a legacy of innovation that inspires future generations to think boldly and act purposefully.",
    visionQuote: "True leadership isn't about being in charge. It's about leaving a legacy that empowers others.",
    trustedBy: "Trusted By Industry Leaders",
    projectsTitle: "Showcase Projects",
    projectDescription: "Trade on TV revolutionizes how investors interact with financial markets through television interfaces. The platform provides intuitive analytics, real-time market data, and seamless trading capabilities optimized for the big screen experience.",
    learnMore: "View Details"
  },
  tr: {
    heroTitle: "BEN BURADA MEMNUN ETMEK İÇİN DEĞİLİM - İLHAM VERMEK VE GÜÇLENDİRMEK İÇİN BURADAYIM",
    heroSubtitle: "Statükoyu zorlayan ve daha iyi bir gelecek inşa eden cesur çözümler yaratmak. Yenilik ve amaç odaklı liderlik sayesinde, vizyoner konseptleri dönüştürücü gerçekliklere dönüştürmek.",
    readMore: "Devamını Oku",
    viewAllProjects: "Tüm Projeleri Görüntüle",
    philosophyTitle: "Vizyon",
    aboutBarkin: "Barkın Sayın hakkında",
    philosophyText1: "Başarı, sadece sayılarla ya da kısa vadeli zaferlerle ölçülen bir kavram değildir; cesaret, kararlılık ve büyümeye duyulan sarsılmaz bir arzuyla sürdürülen sürekli bir yolculuktur. Barkın Sayın, gerçek başarının mükemmellikle değil; özgünlük, vizyon ve geride bırakılan etkiyle ölçüldüğüne inanır.",
    philosophyText2: "Bir girişimci olarak Barkın Sayın, daima kalıcı değer yaratma ilkesiyle hareket etmiştir. Ona göre yenilik; statükoya meydan okuma, geleneksel sınırların ötesine geçme ve kendi yolunu cesaretle çizme ile mümkündür. Bu yolculuk, risk almayı, her adımdan ders çıkarmayı ve her deneyimde büyümeyi gerektirir.",
    philosophyText3: "Hızla değişen bir dünyada başarı; gürültü yaratmak ya da trendleri takip etmekle değil, zamanla yankı uyandıran bir etki yaratmakla ilgilidir. Barkın Sayın, temel değerlerine sadık kalarak zamana meydan okuyan fikirler ve girişimler inşa etmeye odaklanır:",
    quoteText: "Statükoya meydan okuyun – gerçek yenilik, konforun sona erdiği yerde başlar.",
    visionTitle: "Vizyona Giden Yolum",
    visionSubtitle: "Yenilikçi düşünme ve amaçlı liderlik yoluyla zorlukları nasıl ele aldığımı ve anlamlı değişim getiren çözümler yarattığımı keşfedin.",
    visionText: "Girişimci mükemmelliğe kararlı bir odaklanma ve işletme yönetimi konusunda kapsamlı bir uzmanlıkla, çok sayıda projeyi konseptten uygulamaya kadar başarıyla yönlendirdim. Taahhüdüm, yenilikçi fikirleri pratik çözümlere dönüştürmektir. Sürdürülebilir değer yaratma ve geleceği şekillendirme vizyonuyla yönlendirilerek, genel hedefler ve uzun vadeli başarı konusunda net bir bakış açısı sürdürüyorum.",
    visionText2: "Sürdürülebilir büyüme ve olumlu etki yaratmaya kararlıyım. Amacım, gelecek nesillere cesurca düşünmeye ve amaçlı hareket etmeye ilham veren bir yenilik mirası bırakmaktır.",
    visionQuote: "Gerçek liderlik, yönetimde olmakla ilgili değildir. Başkalarını güçlendiren bir miras bırakmakla ilgilidir.",
    trustedBy: "Sektörde Lider Markalar Tarafından Güvenilir",
    projectsTitle: "Güncel Projeler",
    projectDescription: "Trade on TV, televizyon arayüzleri üzerinden finansal piyasalarda sezgisel analiz ve gerçek zamanlı ticaret imkanı sunan yenilikçi platform. Projenin detaylarını görmek için tıklayın.",
    learnMore: "Detayları Gör"
  },
  fr: {
    heroTitle: "JE NE SUIS PAS LÀ POUR PLAIRE - JE SUIS LÀ POUR INSPIRER ET AUTONOMISER",
    heroSubtitle: "Créer des solutions audacieuses qui remettent en question le statu quo et construisent un avenir meilleur. Grâce à l'innovation et au leadership axé sur l'objectif, transformer des concepts visionnaires en réalités transformatrices.",
    readMore: "Lire Plus",
    viewAllProjects: "Voir Tous Les Projets",
    philosophyTitle: "Visión",
    aboutBarkin: "À Propos de Barkin Sayin",
    philosophyText1: "Le succès n'est pas un concept mesuré uniquement par des chiffres ou des victoires à court terme; c'est un voyage continu soutenu par le courage, la détermination et un désir inébranlable de croissance. Barkin Sayin croit que le vrai succès ne se mesure pas à la perfection, mais à l'authenticité, à la vision et à l'impact laissé derrière soi.",
    philosophyText2: "En tant qu'entrepreneur, Barkin Sayin a toujours fonctionné avec le principe de créer une valeur durable. Pour lui, l'innovation consiste à remettre en question le statu quo, à dépasser les limites traditionnelles et à tracer audacieusement sa propre voie. Ce voyage nécessite de prendre des risques, d'apprendre à chaque étape et de grandir avec chaque expérience.",
    philosophyText3: "Dans un monde en évolution rapide, le succès ne consiste pas à faire du bruit ou à suivre des tendances, mais à créer un impact qui résonne au fil du temps. Barkin Sayin se concentre sur la construction d'idées et d'entreprises qui défient le temps en restant fidèle aux valeurs fondamentales:",
    quoteText: "Remettez en question le statu quo - la véritable innovation commence là où le confort se termine.",
    visionTitle: "Mon Chemin Vers La Vision",
    visionSubtitle: "Découvrez comment j'aborde les défis et crée des solutions qui apportent un changement significatif par la pensée novatrice et le leadership réfléchi.",
    visionText: "Avec une concentration dédiée à l'excellence entrepreneuriale et une amplie expertise en gestion d'entreprise, j'ai guidé avec succès numerus projets du concept à la mise en œuvre. Mon engagement est de transformer des idées novatrices en solutions pratiques. Guidé par une vision de création de valeur durable et de façonner l'avenir, je maintiens une perspective claire sur les objectifs généraux et le succès à long terme.",
    visionText2: "Je m'engage pour une croissance durable et un impact positif. Mon but est de laisser un héritage d'innovation qui inspire les générations futures à penser audacieusement et à agir avec détermination.",
    visionQuote: "Le vrai leadership ne consiste pas à être en charge. Se traite de laisser un héritage qui autonomise les autres.",
    trustedBy: "Approuvé Par Les Leaders De L'industrie",
    projectsTitle: "Projets En Vedette",
    projectDescription: "Trade on TV révolutionne la façon dont les investisseurs interagissent avec les marchés financiers via les interfaces télévisuelles. La plateforme fournit des analyses intuitives, des données de marché en temps réel et des capacités de trading fluides optimisées pour l'expérience sur grand écran.",
    learnMore: "Voir Détails"
  },
  es: {
    heroTitle: "NO ESTOY AQUÍ PARA COMPLACER - ESTOY AQUÍ PARA INSPIRAR Y EMPODERAR",
    heroSubtitle: "Creando soluciones audaces que desafían el status quo y construyen un futuro mejor. A través de la innovación y el liderazgo basado en propósitos, convirtiendo conceptos visionarios en realidades transformadoras.",
    readMore: "Leer Más",
    viewAllProjects: "Ver Todos Los Proyectos",
    philosophyTitle: "Vision",
    aboutBarkin: "Acerca de Barkin Sayin",
    philosophyText1: "El éxito no es un concepto medido únicamente por números o victorias a corto plazo; es un viaje continuo sostenido por el coraje, la determinación y un inquebrantable deseo de crecimiento. Barkin Sayin cree que el verdadero éxito no se mide por la perfección, sino por la autenticidad, la visión y el impacto dejado atrás.",
    philosophyText2: "Como emprendedor, Barkin Sayin siempre ha operado con el principio de crear valor duradero. Para él, la innovación consiste en desafiar el status quo, ir más allá de los límites tradicionales y trazar audazmente su propio camino. Este viaje requiere tomar riesgos, aprender de cada paso y crecer con cada experiencia.",
    philosophyText3: "En un mundo que cambia rápidamente, el éxito no consiste en hacer ruido o seguir tendencias, sino en crear un impacto que resuene con el tiempo. Barkin Sayin se enfoca en construir ideas y emprendimientos que desafían el tiempo manteniéndose fieles a los valores fundamentales:",
    quoteText: "Desafía el status quo - la verdadera innovación comienza donde termina la comodidad.",
    visionTitle: "Mi Camino Hacia La Visión",
    visionSubtitle: "Descubre cómo abordo los desafíos y creo soluciones que aportan cambios significativos a través del pensamiento innovador y el liderazgo con propósito.",
    visionText: "Con un enfoque dedicado a la excelencia empresarial y una amplia experiencia en gestión de negocios, j'ai guidé avec éxito numerosos proyectos desde el concepto hasta la implementación. Mi compromiso es transformar ideas innovadoras en soluciones prácticas. Impulsado por una visión de crear valor sostenible y dar forma al futuro, mantengo una perspectiva clara sobre los objetivos generales y el éxito a largo plazo.",
    visionText2: "Estoy comprometido con el crecimiento sostenible y el impacto positivo. Mi propósito es dejar un legado de innovación que inspire a las generaciones futuras a pensar con audacia y actuar con determinación.",
    visionQuote: "El verdadero liderazgo no se trata de estar a cargo. Se trata de dejar un legado que empodere a otros.",
    trustedBy: "Confiado Por Líderes De La Industria",
    projectsTitle: "Proyectos Destacados",
    projectDescription: "Trade on TV revoluciona la forma en que los inversores interactúan con los mercados financieros a través de interfaces de televisión. La plataforma proporciona análisis intuitivos, datos de mercado en tiempo real y capacidades de negociación fluidas optimizadas para la experiencia de pantalla grande.",
    learnMore: "Ver Detalles"
  },
  ar: {
    heroTitle: "أنا لست هنا لإرضاء - أنا هنا لإلهام وتمكين",
    heroSubtitle: "إنشاء حلول جريئة تتحدى الوضع الراهن وتبني مستقبلاً أفضل. من خلال الابتكار والقيادة الهادفة، تحويل المفاهيم الرؤيوية إلى حقائق تحويلية.",
    readMore: "اقرأ المزيد",
    viewAllProjects: "عرض جميع المشاريع",
    philosophyTitle: "الرؤية",
    aboutBarkin: "عن باركين ساين",
    philosophyText1: "النجاح ليس مفهوماً يقاس فقط بالأرقام أو الانتصارات قصيرة المدى؛ إنه رحلة مستمرة تدعمها الشجاعة والتصميم والرغبة الثابتة في النمو. يؤمن باركين ساين أن النجاح الحقيقي لا يقاس بالكمال، بل بالأصالة والرؤية والتأثير المتروك.",
    philosophyText2: "كرائد أعمال، عمل باركين ساين دائماً وفق مبدأ خلق قيمة دائمة. بالنسبة له، الابتكار يتعلق بتحدي الوضع الراهن، وتجاوز الحدود التقليدية، ورسم مسارك الخاص بجرأة. تتطلب هذه الرحلة المخاطرة، والتعلم من كل خطوة، والنمو مع كل تجربة.",
    philosophyText3: "في عالم سريع التغير، النجاح لا يتعلق بإحداث ضجة أو اتباع الاتجاهات، بل بخلق تأثير يتردد صداه مع مرور الوقت. يركز باركين ساين على بناء أفكار ومشاريع تتحدى الزمن من خلال البقاء وفية للقيم الأساسية:",
    quoteText: "تحدى الوضع الراهن - الابتكار الحقيقي يبدأ حيث ينتهي الراحة.",
    visionTitle: "طريقي إلى الرؤية",
    visionSubtitle: "اكتشف كيف أتعامل مع التحديات وأخلق حلولاً تجلب تغييراً هادفاً من خلال التفكير المبتكر والقيادة الهادفة.",
    visionText: "مع تركيز مخصص على التميز في ريادة الأعمال وخبرة شاملة في إدارة الأعمال، قمت بتوجيه العديد من المشاريع بنجاح من المفهوم إلى التنفيذ. التزامي هو تحويل الأفكار المبتكرة إلى حلول عملية. مدفوعاً برؤية لخلق قيمة مستدامة وتشكيل المستقبل، أحافظ على منظور واضح حول الأهداف الشاملة والنجاح طويل المدى.",
    visionText2: "أنا ملتزم بالنمو المستدام والتأثير الإيجابي. هدفي هو ترك إرث من الابتكار يلهم الأجيال القادمة للتفكير بجرأة والتصرف بهدف.",
    visionQuote: "القيادة الحقيقية ليست أن تكون مسؤولاً. إنها تتعلق بترك إرث يمكّن الآخرين.",
    trustedBy: "موثوق به من قبل قادة الصناعة",
    projectsTitle: "مشاريع مميزة",
    projectDescription: "يثور Trade on TV في كيفية تفاعل المستثمرين مع الأسواق المالية من خلال واجهات التلفزيون. توفر المنصة تحليلات بديهية، وبيانات سوق في الوقت الحقيقي، وقدرات تداول سلسة محسنة لتجربة الشاشة الكبيرة.",
    learnMore: "عرض التفاصيل"
  }
};

// Featured Project Pop-up
const PopupContainer = styled(motion.div)`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 380px;
  height: 210px;
  background: white;
  border-radius: 10px;
  padding: 20px 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.05);
  
  @media (max-width: 480px) {
    width: 330px;
    height: 200px;
    bottom: 20px;
    right: 20px;
    padding: 18px 20px;
  }
`;

const PopupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const PopupTitle = styled.h3`
  color: #111;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-transform: uppercase;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(0, 0, 0, 0.4);
  cursor: pointer;
  font-size: 20px;
  padding: 0;
  margin: 0;
  line-height: 1;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: rgba(0, 0, 0, 0.7);
  }
`;

const PopupContent = styled.div`
  display: flex;
  gap: 18px;
  align-items: center;
  height: calc(100% - 40px);
`;

const PopupImage = styled.img`
  width: 90px;
  height: 90px;
  object-fit: contain;
  border-radius: 6px;
  background: white;
  padding: 8px;
`;

const PopupTextContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  padding-top: 2px;
`;

const PopupDescription = styled.p`
  color: #444;
  font-size: 14px;
  line-height: 1.6;
  margin: 0 0 18px 0;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PopupInfo = styled.p`
  color: #666;
  font-size: 11px;
  line-height: 1.4;
  margin: 0 0 10px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-style: italic;
`;

const PopupButton = styled(Link)`
  display: inline-block;
  background: black;
  color: white;
  padding: 10px 16px;
  border-radius: 8px;
  text-decoration: none;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.3s ease;
  letter-spacing: 0.01em;
  text-transform: uppercase;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: #222;
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

// Main Component
const Home = () => {
  const { language } = useLanguage();
  const homeMetadata = getHomeMetadata(language);
  const [isVisible, setIsVisible] = useState({
    hero: false,
    philosophy: false,
    vision: false,
    projects: false
  });
  
  const [showPopup, setShowPopup] = useState(false);
  const [hideHero, setHideHero] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  
  // Kullanıcı etkileşimi olup olmadığını takip etmek için state
  const [userInteracted, setUserInteracted] = useState(false);
  // Video pop-up'ını göstermek için state
  const [showVideoPopup, setShowVideoPopup] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  
  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Sayfa yüklendiğinde kullanıcı etkileşimlerini dinle
  useEffect(() => {
    const handleUserInteraction = () => {
      setUserInteracted(true);
    };

    // Kullanıcı etkileşimlerini dinle
    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('touchstart', handleUserInteraction);
    document.addEventListener('keydown', handleUserInteraction);

    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
    };
  }, []);

  useEffect(() => {
    const closeVideoHandler = () => handleCloseVideo();
    document.addEventListener('closeVideo', closeVideoHandler);
    
    return () => {
      document.removeEventListener('closeVideo', closeVideoHandler);
    };
  }, []);
  
  // Video ses seviyesini ayarlama işlevi
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const volume = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.volume = volume;
    }
  };
  // Videoyu kapatma işlevi
  const handleCloseVideo = (e?: React.MouseEvent<HTMLButtonElement> | Event) => {
    if (e && 'preventDefault' in e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log("Video kapatıldı");
    
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0; // Videoyu başa sar
    }
    
    setShowVideo(false);
    setShowVideoPopup(true); // Thumbnail'i tekrar göster
    
    // Arkaplanı normale döndür
    setTimeout(() => {
      setHideHero(false);
    }, 300);
  };

  // Sayfa yüklendiğinde çalışacak işlevler
  useEffect(() => {
    // Video pop-up'ı göster
    setShowVideoPopup(true);
    
    // 10 saniye sonra pop-up'ı göster
    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 10000);
    
    // Sayfa değiştiğinde veya kapatıldığında videoyu durdur
    return () => {
      clearTimeout(timer);
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    };
  }, []);

  const heroRef = useRef<HTMLDivElement>(null);
  const philosophyRef = useRef<HTMLDivElement>(null);
  const visionRef = useRef<HTMLDivElement>(null);
  const projectsRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const target = entry.target;
          
          if (target === heroRef.current) {
            setIsVisible(prev => ({ ...prev, hero: entry.isIntersecting }));
          } else if (target === philosophyRef.current) {
            setIsVisible(prev => ({ ...prev, philosophy: entry.isIntersecting }));
          } else if (target === visionRef.current) {
            setIsVisible(prev => ({ ...prev, vision: entry.isIntersecting }));
          } else if (target === projectsRef.current) {
            setIsVisible(prev => ({ ...prev, projects: entry.isIntersecting }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px' }
    );
    
    // Observe all section refs
    if (heroRef.current) observer.observe(heroRef.current);
    if (philosophyRef.current) observer.observe(philosophyRef.current);
    if (visionRef.current) observer.observe(visionRef.current);
    if (projectsRef.current) observer.observe(projectsRef.current);
    
    return () => {
      observer.disconnect();
    };
  }, []);
  
  // Function to get text in the current language
  const getText = (key: TranslationKeys): string => {
    if (language === 'en' || language === 'tr' || language === 'fr' || language === 'es' || language === 'ar') {
      return homeTranslations[language][key];
    }
    return key;
  };
  
  // Create a direct video URL based on the environment 
  const getVideoUrl = () => {
    // Use an absolute path for the video
    return '/assets/videos/Trade on TV Founder.MOV';
  };
  
  // Video quality state and mapping
  const [quality, setQuality] = useState<'1080p' | '720p' | '360p'>('1080p');
  const videoSources: Record<string, string> = {
    '1080p': '/video-1080p.mp4',
    '720p': '/video-720p.mp4',
    '360p': '/video-360p.mp4',
  };
  const handleQualityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newQuality = e.target.value as '1080p' | '720p' | '360p';
    setQuality(newQuality);
    if (videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      videoRef.current.src = videoSources[newQuality];
      videoRef.current.load();
      videoRef.current.currentTime = currentTime;
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => { /* ignore interruption */ });
      }
    }
  };

  return (
    <HomeContainer>
      <SEO />
      
      <HeroSection ref={heroRef}>
        <HeroImageContainer $isHiding={hideHero}>
          <HeroPngImage src={heroBackground} alt="Hero Background" />
        </HeroImageContainer>
        <Decorator 
          className="middle-center" 
          initial={{ opacity: 0, scale: 0.8 }} 
          animate={{ opacity: hideHero ? 0 : 0.08, scale: 1 }}
          transition={{ duration: 2 }}
        />
        <HeroContent
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: hideHero ? 0 : 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          <HeroTitle>
            <motion.span
              className="text-reveal"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }} /* Always keep text visible */
              transition={{ 
                duration: 1.5,
                ease: "easeOut",
                delay: 0.5
              }}
            >
            NOT HERE TO PLEASE - I'M HERE TO INSPIRE AND EMPOWER
            </motion.span>
          </HeroTitle>
        </HeroContent>
        
        {/* Video thumbnail ve izleme butonu */}
        {showVideoPopup && (
          <VideoPopup>
            {!isMobile && <VideoPopupTitle>Watch BARKIN SAYIN</VideoPopupTitle>}
            <WatchButton onClick={() => {
              setHideHero(true); // Arkaplanı koyulaştır
              setShowVideo(true); // Videoyu göster
              setShowVideoPopup(false); // Thumbnail'i gizle
              
              // Video elementini ayarla ve oynat
              setTimeout(() => {
                if (videoRef.current) {
                  try {
                    // Videoyu başa sar
                    videoRef.current.currentTime = 0;
                    
                    // Video ayarlarını yap
                    videoRef.current.autoplay = true;
                    videoRef.current.muted = false; // Ses açık olsun
                    videoRef.current.volume = 0.7; // Varsayılan ses seviyesi
                    videoRef.current.controls = false;
                    videoRef.current.playsInline = true;
                    
                    // Videoyu oynat
                    const playPromise = videoRef.current.play();
                    
                    if (playPromise !== undefined) {
                      playPromise.then(() => {
                        console.log("Video başarıyla oynatılıyor");
                      }).catch(error => {
                        console.error("Video oynatma hatası:", error);
                      });
                    }
                    
                    // Video bittiğinde
                    videoRef.current.onended = () => {
                      handleCloseVideo();
                    };
                  } catch (error) {
                    console.error("Video ayarlama hatası:", error);
                  }
                }
              }, 300);
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 3L19 12L5 21V3Z" fill="black" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {isMobile ? "Watch" : "Watch Now"}
            </WatchButton>
          </VideoPopup>
        )}
        
        {/* Video konteyner */}
        <HeroVideoContainer $isVisible={showVideo}>
          {/* Video kontrolleri */}
          <VideoControls>
            <VideoCloseBtn onClick={handleCloseVideo}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </VideoCloseBtn>
            <VolumeControl>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M15.54 8.46C16.4774 9.39764 17.004 10.6692 17.004 11.995C17.004 13.3208 16.4774 14.5924 15.54 15.53" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input 
                type="range" 
                min="0" 
                max="1" 
                step="0.1" 
                defaultValue="0.7" 
                onChange={handleVolumeChange} 
              />
            </VolumeControl>
            {/* Video kalite seçici */}
            <QualitySelector value={quality} onChange={handleQualityChange}>
              <option value="1080p">1080p</option>
              <option value="720p">720p</option>
              <option value="360p">360p</option>
            </QualitySelector>
          </VideoControls>
          <VideoElement 
            ref={videoRef}
            src={videoSources[quality]}
            playsInline
            autoPlay={showVideo} // Sadece video gösterildiğinde otomatik oynat
            muted={false} // Ses açık olsun
            controls={false}
            preload="none" // Videoyu kullanıcı tıklayana kadar yükleme
            loop={false}
            disablePictureInPicture 
            disableRemotePlayback
          />
        </HeroVideoContainer>
      </HeroSection>

      <PhilosophySection ref={philosophyRef}>
        <PatternBackground />
        <PhilosophyContainer>
          <PhilosophyContent>
            <SectionHeading
              $themeMode="light"
              initial={{ opacity: 0, y: 30 }}
              animate={isVisible.philosophy ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              {getText('philosophyTitle')}
            </SectionHeading>
            <PhilosophyText
              initial={{ opacity: 0, y: 30 }}
              animate={isVisible.philosophy ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {getText('philosophyText1')}
            </PhilosophyText>
            <PhilosophyQuote>
              {getText('quoteText')}
            </PhilosophyQuote>
            <PhilosophyText
              initial={{ opacity: 0, y: 30 }}
              animate={isVisible.philosophy ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {getText('philosophyText2')}
            </PhilosophyText>
            <PhilosophyText
              initial={{ opacity: 0, y: 30 }}
              animate={isVisible.philosophy ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              {getText('philosophyText3')}
            </PhilosophyText>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isVisible.philosophy ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <Link to="/about">
                <PrimaryButton 
                  whileHover={{ scale: 1.01 }} 
                  whileTap={{ scale: 0.99 }}
                >
                  {getText('learnMore')}
                </PrimaryButton>
              </Link>
            </motion.div>
          </PhilosophyContent>
          <PhilosophyImageWrapper
            initial={{ opacity: 0, x: 30 }}
            animate={isVisible.philosophy ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <div className="sc-huvZk jkiBpf" style={{ transform: 'none', width: '100%', height: '100%', position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img 
                src="/images/BARKIN SAYIN.webp" 
                alt="BARKIN SAYIN" 
                loading="lazy"
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover', 
                  objectPosition: 'center',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  minWidth: '100%',
                  minHeight: '100%'
                }} 
              />
            </div>
          </PhilosophyImageWrapper>
        </PhilosophyContainer>
      </PhilosophySection>
      
      <VisionSection ref={visionRef}>
        <VisionContainer>
          <VisionHeader>
            <VisionTitle
              initial={{ opacity: 0, y: 30 }}
              animate={isVisible.vision ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              {getText('visionTitle')}
            </VisionTitle>
            <VisionSubtitle
              initial={{ opacity: 0, y: 30 }}
              animate={isVisible.vision ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {getText('visionSubtitle')}
            </VisionSubtitle>
          </VisionHeader>
          
          <VisionContent>
            <VisionImageWrapper
              initial={{ opacity: 0, x: -30 }}
              animate={isVisible.vision ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <VisionImage 
                style={{ backgroundImage: `url(${visionImage})` }}
                animate={isVisible.vision ? { scale: 1 } : { scale: 0.98 }}
                transition={{ duration: 0.5 }}
                whileHover={{ scale: 1.01 }}
              />
            </VisionImageWrapper>
            
            <VisionTextContainer>
              <VisionText
                initial={{ opacity: 0, x: 30 }}
                animate={isVisible.vision ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8 }}
              >
                {getText('visionText')}
              </VisionText>
              
              <VisionText
                initial={{ opacity: 0, x: 30 }}
                animate={isVisible.vision ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                {getText('visionText2')}
              </VisionText>
              
              <VisionQuote
                initial={{ opacity: 0, x: 30 }}
                animate={isVisible.vision ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                {getText('visionQuote')}
              </VisionQuote>
              
              <VisionCTA
                initial={{ opacity: 0, x: 30 }}
                animate={isVisible.vision ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <PrimaryButton
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <Link to="/projects">{getText('viewAllProjects')}</Link>
                </PrimaryButton>
              </VisionCTA>
            </VisionTextContainer>
          </VisionContent>
        </VisionContainer>
      </VisionSection>

      <ProjectsSection ref={projectsRef}>
        <WaveDivider />
        <BrandsContainer>
          <BrandsTitle>{getText('trustedBy')}</BrandsTitle>
          <BrandsSlider>
            <BrandsTrack
              animate={scrollVariant.animate}
            >
              <BrandLogoWrapper>
              <BrandLogo 
                src={appleLogo}
                alt="Apple"
                loading="lazy"
              />
            </BrandLogoWrapper>
              <BrandLogoWrapper>
              <BrandLogo 
                src={partner2Logo}
                alt="Partner 2"
                loading="lazy"
              />
            </BrandLogoWrapper>
              <BrandLogoWrapper>
              <BrandLogo 
                src={partner4Logo}
                alt="Partner 4"
                loading="lazy"
              />
            </BrandLogoWrapper>
              <BrandLogoWrapper>
              <BrandLogo 
                src={partner7Logo}
                alt="Partner 7"
                loading="lazy"
              />
            </BrandLogoWrapper>
              {/* Placeholder for Samsung logo */}
              <BrandLogoWrapper>
                <BrandLogo 
                  src={appleLogo} // Will be replaced with Samsung
                  alt="Samsung"
                  loading="lazy"
                />
              </BrandLogoWrapper>
              {/* Placeholder for Ionos logo */}
              <BrandLogoWrapper>
                <BrandLogo 
                  src={partner2Logo} // Will be replaced with Ionos
                  alt="Ionos"
                  loading="lazy"
                />
              </BrandLogoWrapper>
              {/* Placeholder for AWS logo */}
              <BrandLogoWrapper>
                <BrandLogo 
                  src={partner4Logo} // Will be replaced with AWS
                  alt="AWS"
                  loading="lazy"
                />
              </BrandLogoWrapper>
              
              {/* New Brand Logo 1 */}
              <BrandLogoWrapper>
                <BrandLogo 
                  src={partner1Logo}
                  alt="Partner 1"
                  loading="lazy"
                />
              </BrandLogoWrapper>
              {/* New Brand Logo 2 */}
              <BrandLogoWrapper>
                <BrandLogo 
                  src={partner5Logo}
                  alt="Partner 5"
                  loading="lazy"
                />
              </BrandLogoWrapper>
              {/* New Brand Logo 3 */}
              <BrandLogoWrapper>
                <BrandLogo 
                  src={partner6Logo}
                  alt="Partner 6"
                  loading="lazy"
                />
              </BrandLogoWrapper>
              
              {/* Duplicate logos to create seamless loop */}
              <BrandLogoWrapper>
                <BrandLogo 
                  src={appleLogo}
                  alt="Apple"
                  loading="lazy"
                />
              </BrandLogoWrapper>
              <BrandLogoWrapper>
                <BrandLogo 
                  src={partner2Logo}
                  alt="Partner 2"
                  loading="lazy"
                />
              </BrandLogoWrapper>
              <BrandLogoWrapper>
                <BrandLogo 
                  src={partner4Logo}
                  alt="Partner 4"
                  loading="lazy"
                />
              </BrandLogoWrapper>
              <BrandLogoWrapper>
                <BrandLogo 
                  src={partner7Logo}
                  alt="Partner 7"
                  loading="lazy"
                />
              </BrandLogoWrapper>
            </BrandsTrack>
          </BrandsSlider>
        </BrandsContainer>
      </ProjectsSection>

      <AnimatePresence>
        {/* Trade on TV Pop-up - Video açık değilken göster */}
        {showPopup && !showVideo && (
          <PopupContainer
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 100, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <PopupHeader>
              <PopupTitle>Trade on TV</PopupTitle>
              <CloseButton onClick={() => setShowPopup(false)}>×</CloseButton>
            </PopupHeader>
            <PopupContent>
              <PopupImage src={totBlackLogo} alt="Trade on TV" />
              <PopupTextContent>
                <PopupDescription>
                  {language === 'tr' 
                    ? "Televizyon arayüzleri üzerinden finansal piyasalarda sezgisel analiz ve gerçek zamanlı ticaret imkanı sunan yenilikçi platform. Projenin detaylarını görmek için tıklayın."
                    : "Innovative platform offering intuitive analytics and real-time trading capabilities through television interfaces. Click to explore project details and see how it transforms financial market access."}
                </PopupDescription>
                <PopupButton as={Link} to="/projects">
                  {getText('learnMore')}
                </PopupButton>
              </PopupTextContent>
            </PopupContent>
          </PopupContainer>
        )}
      </AnimatePresence>
    </HomeContainer>
  );
};

export default Home;
